import { useCallback, useEffect } from 'react';
import { useApolloClient, useQuery } from 'react-apollo';
import { useDispatch, useSelector } from 'react-redux';

import { MeWithIdentities } from '../../../../../../../../graphql/queries/account';
import { IDENTITY_RELATIONSHIP } from '../../../../../../../../constants/identity';
import { getSimplifiedAlbumMembershipGroupQuery } from '../../../../../../../temp/views/memberships/AlbumMembershipGroup/graphql/queries/getSimplifiedAlbumMembershipGroup';
import { setActiveIdentity as setActiveIdentityAction } from '../../../../../../../../services/checkout/redux/checkoutActions';
import { getHighestMatchedPhotosIdentity } from '../helpers';

/**
 * Sets the active identity to the first available from either the user or membership group
 *
 * @param {Object} options The hook options
 * @param {String} options.albumMembershipGroupId The albumMembershipGroupId
 */
const useFirstAvailableIdentity = ({ albumMembershipGroupId } = {}) => {
  const client = useApolloClient();

  const {
    data: { albumMembershipGroup = {} } = {},
    loading: loadingAlbumMembershipGroup,
  } = useQuery(getSimplifiedAlbumMembershipGroupQuery, {
    variables: {
      id: albumMembershipGroupId,
    },
    skip: !albumMembershipGroupId,
    fetchPolicy: 'cache-first',
  });

  const dispatch = useDispatch();
  const identities = albumMembershipGroup?.identities;
  const { activeIdentity } = useSelector(({ checkout }) => checkout);
  const setActiveIdentity = useCallback(
    identity => dispatch(setActiveIdentityAction(identity)),
    [dispatch],
  );

  useEffect(() => {
    const setFirstAvailableIdentity = async () => {
      const joinedIdentity = getHighestMatchedPhotosIdentity(identities);

      if (joinedIdentity) {
        setActiveIdentity(joinedIdentity?.identity);
        return;
      }

      const { data: { me } = {} } = await client.query({
        query: MeWithIdentities,
      });

      const linkedIdentities = me?.linked_identities?.records;
      const primaryIdentity = (linkedIdentities ?? []).find(
        identity => identity.relationship === IDENTITY_RELATIONSHIP.PRIMARY,
      );
      if (primaryIdentity) {
        setActiveIdentity(primaryIdentity);
      }
    };

    const activeIdentityIsMember = !!(identities ?? []).find(
      joinedIdentity => joinedIdentity.identity.id === activeIdentity?.id,
    );

    if (
      (!activeIdentity || !activeIdentityIsMember) &&
      !loadingAlbumMembershipGroup
    ) {
      setFirstAvailableIdentity();
    }
  }, [loadingAlbumMembershipGroup]);
};

export default useFirstAvailableIdentity;
